/**
 * Drawing everybody else.
 *
 * One mesh per peer, built from the graph its owner broadcast, moved each frame
 * to wherever interpolation says it is. Nothing here decides anything: peers
 * arrive from the room, and this puts them on screen.
 *
 * ── The floating origin ─────────────────────────────────────────────────────
 *
 * The one thing that must not be got wrong. Every client shifts the world so its
 * own ship sits at zero, and the room speaks in absolutes — so a remote ship is
 * drawn at `theirAbsolute − origin`, and `origin` is this frame's, not last
 * frame's.
 *
 * This is the same class of bug that cost three rounds on station collisions,
 * and it is silent: nothing throws, ships are simply somewhere else. It is one
 * subtraction and it lives in one place, here, for exactly that reason.
 */

import * as THREE from 'three';
import type { ShipGraph } from '@interspacial/shared';

import { SHIP_DEPTH, shipExtentOf, type ShipExtent } from '@interspacial/shared';

import { buildShipMesh, type ShipMesh } from './shipMesh';
import type { Berth } from './systemScene';
import { interpolate, type Peer, type PeerFeed } from '../net/peers';

/**
 * How far away a ship stops being part of the game, in world units.
 *
 * Inside this, remote ships are real and will — once ships can hurt each other —
 * interact. Beyond it they are scenery: still drawn, because a system is a
 * lonely place with nothing moving in it, but not participating.
 *
 * This is also what disposes of the floating-point question. Two clients
 * simulating independently will disagree, and the disagreement grows with
 * distance; capping interaction at a range where it is still small means the
 * divergence only ever exists where nobody can see it.
 *
 * **Nothing enforces this yet**, because nothing interacts with a remote ship:
 * they are drawn and that is all they do. It is written down here so the rule
 * has one home by the time weapons and ship-to-ship collision need it, rather
 * than being rediscovered as a number in somebody's hit test.
 */
export const INTERACTION_RANGE = 8000;

interface Drawn {
  mesh: ShipMesh;
  /** The graph revision this mesh was built from. */
  revision: number;
  /** Whether it was built standing on its gear. Docked ships are. */
  landed: boolean;
  /**
   * How far the hull reaches, measured once from the mesh it was built from.
   *
   * Kept because a parked ship is an obstacle, and the flight loop needs its
   * size every frame to test against. Measuring it per frame would mean walking
   * the geometry sixty times a second for a number that cannot change without
   * the mesh being rebuilt anyway.
   */
  extent: ShipExtent;
}

/** A ship sitting on a pad, as something to run into. */
export interface ParkedShip {
  stationId: string;
  padId: string;
  /** World units, the same scale the player's own hull is measured in. */
  extent: ShipExtent;
}

/**
 * Where a pad is, in scene space, or null if this client cannot say.
 *
 * Docked ships are drawn from their berth rather than from a position, because
 * stations orbit and a remembered landing point drifts away from the pad it was
 * taken on. The caller owns the station scene, so the caller does the lookup.
 *
 * Scene space, already rebased -- a station's world matrix in the scene graph
 * has the floating origin applied, so this does not go through the subtraction
 * below.
 */
export type PadLookup = (
  stationId: string,
  padId: string,
) => { position: THREE.Vector3; quaternion: THREE.Quaternion; standoff: number } | null;

/**
 * Where a mass-lock anchor is, in absolute coordinates, or null if unknown.
 *
 * A ship holding station beside an orbiting body sends its offset from that
 * body rather than an absolute point, so the fast part of its motion -- the
 * orbit -- is resolved here, at frame rate, instead of being carried by ten
 * samples a second. That is the difference between a steady ship and one that
 * shakes at the beat between the two rates.
 */
export type AnchorLookup = (label: string) => THREE.Vector3 | null;

export interface RemoteShips {
  /**
   * Put every peer where it belongs this frame.
   *
   * `origin` is the point the world has been shifted by — the player's own
   * position — and `now` is on the same clock the feed was given.
   */
  update: (
    feed: PeerFeed | null,
    origin: THREE.Vector3,
    now: number,
    padAt?: PadLookup,
    anchorAt?: AnchorLookup,
  ) => void;
  /**
   * Everyone currently drawn and not parked, in scene space.
   *
   * For the scanner. Read from the drawn meshes rather than from the feed
   * because a ship this client could not place is not one it should be putting
   * a marker on — and because the mesh's position is already the answer, after
   * interpolation and after whatever frame the sender was measuring from.
   */
  flying: () => { id: string; callsign: string; at: THREE.Vector3 }[];
  /**
   * Everyone currently parked, and how big they are.
   *
   * For collision and for pad occupancy. Read from what is actually *drawn*
   * rather than from the feed: a ship this client cannot place -- unknown
   * station, unknown pad -- is not something it should be able to hit either.
   */
  parked: () => ParkedShip[];
  dispose: () => void;
}

/**
 * Ships are drawn in flight configuration.
 *
 * `landed: false` stows the gear. A docked peer sitting on a pad with its legs
 * up is wrong, but it is wrong in a way nobody can see from the outside of a
 * station, and the alternative is publishing a landing state that exists only to
 * change how somebody else's legs look.
 */
function build(graph: ShipGraph, landed: boolean): ShipMesh {
  return buildShipMesh(graph, { landed });
}

/**
 * The two lookups a scene needs to place other people's ships.
 *
 * ── Why this is shared rather than written twice ────────────────────────────
 *
 * It *was* written twice, once in each view, and both copies were coordinate
 * conversions -- the category of bug that has cost this project more time than
 * any other, because a frame mistake throws nothing and simply puts things
 * somewhere plausible and wrong. Two copies of a conversion drift; one does not.
 *
 * The only real difference between the views is the floating origin: the cockpit
 * shifts the world so the player sits at zero, and the berth does not, because
 * its camera is parked on a deck instead. That difference is this one argument.
 *
 * ── The two frames, and why they differ ─────────────────────────────────────
 *
 * `padAt` answers in **scene** space, because a pad is a place in the scene and
 * the mesh goes straight there. `anchorAt` answers in **absolute** space,
 * because it is added to an offset that arrived over the wire, and the wire
 * speaks absolutes. Getting these the same way round would be the bug this
 * exists to prevent, so they are named in the types and stated here.
 */
export function sceneLookups(
  built: { berths: readonly Berth[]; focusables: readonly { label: string; node: THREE.Object3D }[] },
  origin: THREE.Vector3,
): { padAt: PadLookup; anchorAt: AnchorLookup } {
  // Reused across calls and across frames. Neither result escapes further than
  // the caller's next line, and both are read before the next lookup runs.
  const padScratch = new THREE.Vector3();
  const padSpin = new THREE.Quaternion();
  const anchorScratch = new THREE.Vector3();

  return {
    padAt: (stationId, padId) => {
      const berth = built.berths.find(
        (candidate) => candidate.stationId === stationId && candidate.padId === padId,
      );
      // Not a pad this client knows about. Undrawn beats drawn in the wrong
      // place -- a hull floating beside a station reads as a bug, which it is.
      if (!berth) return null;

      padScratch.copy(berth.local);
      berth.deck.localToWorld(padScratch);
      berth.deck.getWorldQuaternion(padSpin);

      return { position: padScratch, quaternion: padSpin, standoff: 0 };
    },

    anchorAt: (label) => {
      const focus = built.focusables.find((candidate) => candidate.label === label);
      if (!focus) return null;

      // Scene space plus the shift is absolute, which is what the sender's
      // offset was measured against. Zero shift in a view that does not rebase.
      return focus.node.getWorldPosition(anchorScratch).add(origin);
    },
  };
}

export function createRemoteShips(scene: THREE.Scene): RemoteShips {
  const drawn = new Map<string, Drawn>();
  const group = new THREE.Group();
  scene.add(group);

  const drop = (id: string) => {
    const existing = drawn.get(id);
    if (!existing) return;
    group.remove(existing.mesh.group);
    existing.mesh.dispose();
    drawn.delete(id);
  };

  const place = (
    peer: Peer,
    origin: THREE.Vector3,
    now: number,
    padAt?: PadLookup,
    anchorAt?: AnchorLookup,
  ) => {
    // No graph yet, or in the tunnel. Either way there is nothing to draw, and
    // a peer that goes quiet must not leave a ship hanging in space.
    if (!peer.graph || peer.hidden) {
      drop(peer.id);
      return;
    }

    /*
     * Where a parked ship goes.
     *
     * Resolved every frame rather than once, because the pad is moving: a
     * station orbits, and a ship bolted to it has to orbit with it. Looking it
     * up per frame is a matrix read against a scene the caller already keeps.
     *
     * Null means this client cannot place it -- the station is not in its scene,
     * or the pad is not one it knows. Not drawn beats drawn in the wrong place.
     */
    const berth = peer.docked ? (padAt?.(peer.stationId, peer.padId) ?? null) : null;
    if (peer.docked && !berth) {
      drop(peer.id);
      return;
    }

    let entry = drawn.get(peer.id);

    /*
     * Rebuilt when the hull changes shape, and when it puts its legs down.
     *
     * The revision counts the first; `landed` is the second. A ship that lands
     * has to be redrawn standing on its gear, and one that leaves has to stow
     * it -- the two configurations are different geometry, not a toggle.
     */
    if (entry && (entry.revision !== peer.revision || entry.landed !== peer.docked)) {
      drop(peer.id);
      entry = undefined;
    }

    if (!entry) {
      const mesh = build(peer.graph, peer.docked);
      entry = {
        mesh,
        revision: peer.revision,
        landed: peer.docked,
        extent: shipExtentOf(new THREE.Box3().setFromObject(mesh.group), SHIP_DEPTH / 2),
      };
      // Whose ship this is, on the object itself. Nothing reads it in flight,
      // but a ship in a scene graph that cannot say who it belongs to is a
      // miserable thing to debug — and it is what the tests find them by.
      entry.mesh.group.userData['peerId'] = peer.id;
      entry.mesh.group.userData['ownerUuid'] = peer.ownerUuid;
      entry.mesh.group.userData['callsign'] = peer.callsign;
      group.add(entry.mesh.group);
      drawn.set(peer.id, entry);
    }

    if (berth) {
      /*
       * Sitting on the plate.
       *
       * Already scene space -- the deck's world matrix carries the floating
       * origin -- so this does not go through the subtraction below. Lifted by
       * the hull's own standoff so it rests on its feet rather than in the deck.
       */
      entry.mesh.group.position.copy(berth.position);
      entry.mesh.group.quaternion.copy(berth.quaternion);
      entry.mesh.group.translateZ(entry.mesh.standoff);
      return;
    }

    interpolate(peer, now, entry.mesh.group);

    /*
     * Out of the anchor's frame and back into absolute coordinates.
     *
     * The sample was an offset from a body, and the body has moved since -- so
     * this reads where it is *now*, which is the point. An anchor this client
     * cannot resolve leaves the offset standing on its own, which would put the
     * ship near the origin; better to leave it where it last was than to move
     * it somewhere definitely wrong.
     */
    const from = peer.latest.anchor ? (anchorAt?.(peer.latest.anchor) ?? null) : null;
    if (peer.latest.anchor && !from) {
      drop(peer.id);
      return;
    }
    if (from) entry.mesh.group.position.add(from);

    // Absolute, into scene space. The whole reason this module exists.
    entry.mesh.group.position.sub(origin);
  };

  /** Where each drawn ship is parked, or null for the ones that are flying. */
  const berths = new Map<string, { stationId: string; padId: string }>();

  return {
    flying: () =>
      [...drawn.entries()]
        .filter(([id]) => !berths.has(id))
        .map(([id, entry]) => ({
          id,
          callsign: entry.mesh.group.userData['callsign'] as string,
          at: entry.mesh.group.position,
        })),

    parked: () =>
      [...drawn.entries()].flatMap(([id, entry]) => {
        const where = berths.get(id);
        return where ? [{ ...where, extent: entry.extent }] : [];
      }),

    update: (feed, origin, now, padAt, anchorAt) => {
      if (!feed) {
        // No room — left, jumped, or never joined. Everyone goes.
        for (const id of [...drawn.keys()]) drop(id);
        berths.clear();
        return;
      }

      berths.clear();
      for (const peer of feed.peers.values()) {
        if (peer.docked && peer.stationId) {
          berths.set(peer.id, { stationId: peer.stationId, padId: peer.padId });
        }
      }

      for (const peer of feed.peers.values()) place(peer, origin, now, padAt, anchorAt);

      // Anyone the feed has forgotten. Cheap while a system holds a handful of
      // ships; when interest management arrives this is the loop that will need
      // to stop being a scan.
      for (const id of [...drawn.keys()]) {
        if (!feed.peers.has(id)) drop(id);
      }
    },

    dispose: () => {
      for (const id of [...drawn.keys()]) drop(id);
      berths.clear();
      scene.remove(group);
    },
  };
}
